import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import _ from 'lodash';
import { CardSettingsContext } from '../../../../contexts/card/settings/context';
import { useSetting } from '../../../../utilities/sharedHooks/settings';
import {
  CARD_DELIVERY_TYPE,
  DELIVERY_TYPE_STATUS,
  REPORT_CARD_STATUS,
} from '../../../../utilities/constants';
import {
  CARD_GALILEO_REPORT_DAMAGED,
  CARD_GALILEO_REPORT_LOST,
  CARD_GALILEO_REPORT_STOLEN,
  CARD_ORDER_REPLACEMENT,
} from '../../mutations';
import { showAlert } from '../../../../utilities/notifications';
import MainBodyLayout from '../partials/MainBodyLayout';
import { TermsSettingsUrls } from '../../constants';
import { cardSettingsScreens } from '../constants';
import RadioButton from '../../../../components/RadioButtonGroup';
import TermsCheckbox from '../../../../components/TermsCheckbox';

const ConfirmDeliveryType = () => {
  const { t: translate } = useTranslation();
  const { state, openScreen } = useContext(CardSettingsContext);
  const history = useHistory();

  const cardProvider = state?.user?.cardProvider;
  const cardStatus = state?.cardStatus;
  const screenOptions = state?.screenOptions;

  const [securityOptions, setSecurityOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [termAndConditionChecked, setTermAndConditionChecked] = useState(false);
  const [agreementUrl] = useSetting('content_urls', 'cardholder_term_galileo_us_user');
  const [replacementFee] = useSetting(
    'card_fees',
    `card_fee_${cardProvider}_expedited_replacement`
  );

  useEffect(() => setSecurityOptions(CARD_DELIVERY_TYPE[cardProvider]), [cardProvider]);

  const cardTermsUrl = useMemo(() => TermsSettingsUrls.CARD_TERMS_CONTENT_URL, []);

  const reportCardKey = useMemo(() => {
    if (cardStatus?.is_replacement_requested === false) {
      return CARD_ORDER_REPLACEMENT;
    }

    if (screenOptions) {
      if (screenOptions.state === REPORT_CARD_STATUS.STOLEN) {
        return CARD_GALILEO_REPORT_STOLEN;
      }
      if (screenOptions.state === REPORT_CARD_STATUS.LOST) {
        return CARD_GALILEO_REPORT_LOST;
      }
      if (screenOptions.state === REPORT_CARD_STATUS.DAMAGEDNOTWORK) {
        return CARD_GALILEO_REPORT_DAMAGED;
      }
    }

    return null;
  }, [cardStatus?.is_replacement_requested, screenOptions]);

  const closeFlow = () => {
    history.push('/dashboard/card/details');
  };

  const { mutate: reportGalileoCard } = useMutation(reportCardKey, {
    onSettled: (data, error) => {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else {
        showAlert({
          message: translate('confirm_card_replacement_succes_expedited'),
          type: 'success',
        });
        closeFlow();
      }
    },
  });

  const handleOnChange = (item) => {
    const _securityOptions = securityOptions.map((option) => {
      if (option.state === item.state) {
        const _selectedOption = {
          ...option,
          selected: true,
        };
        setSelectedOption(_selectedOption);
        return _selectedOption;
      }
      return {
        ...option,
        selected: false,
      };
    });
    setSecurityOptions(_securityOptions);
  };

  return (
    <MainBodyLayout
      withBack
      onPrevClick={() =>
        openScreen(cardSettingsScreens.CONFIRM_REPLACEMENT_CARD_DETAILS, screenOptions)
      }
      title={translate('card_order_delivery_type_title')}>
      <div className="my-4">
        <p className="text-f5 text-dark p-0">{translate('card_order_delivery_type_subtitle')}</p>
      </div>
      <div style={{ height: '70%' }} className="mb-4">
        <RadioButton
          data={securityOptions}
          onChange={handleOnChange}
          fees={replacementFee.toFixed(2)}
        />
      </div>
      <div className="mt-5">
        <TermsCheckbox
          contentUrlKey={cardTermsUrl}
          checked={termAndConditionChecked}
          onChange={() => setTermAndConditionChecked((prev) => !prev)}>
          {translate('before_cardholder_agreement_checkbox_link_label')}{' '}
          <a
            target="_blank"
            className="link text-decoration-none"
            href={agreementUrl || '#'}
            download={translate('cryptodraft_cardholder_agreement_title')}
            rel="noreferrer">
            {translate('cardholder_agreement_checkbox_link_label')}
          </a>{' '}
        </TermsCheckbox>
      </div>
      <div
        className={`d-flex flex-row mt-5 pt-3 ${
          isMobile ? 'position-fixed w-95 bg-light pb-3 pe-2' : 'mx-2 px-5'
        }`}
        style={{ bottom: '0', left: '0', zIndex: 100 }}>
        <button
          type="button"
          className="btn py-3 btn-primary w-100"
          disabled={!termAndConditionChecked || _.isNull(selectedOption)}
          onClick={() => {
            if (selectedOption.state === DELIVERY_TYPE_STATUS.STANDARD) {
              reportGalileoCard({
                order_replacement: true,
                use_expedited_shipping: false,
              });
            } else {
              openScreen(cardSettingsScreens.CONFIRM_EXPEDITED_AMOUNT);
            }
          }}>
          {translate('confirm_card_replacement')}
        </button>
      </div>
    </MainBodyLayout>
  );
};

export default ConfirmDeliveryType;
